import { identity } from 'lodash';

import { getRunIdentificationPair } from '@neptune/shared/entity-domain';
import { makeEntityIdentifier } from '@neptune/shared/entity-util';
import { LeaderboardEntry } from '@neptune/shared/leaderboard-domain';

export type CompareMetadata = Map<string, { index: number }>;

export function entriesToCompareMetadata(entries: LeaderboardEntry[]): CompareMetadata {
  const metadata = new Map();

  entries.forEach((run, index) => {
    const runIdentificationPair = getRunIdentificationPair(run);

    if (runIdentificationPair) {
      metadata.set(
        makeEntityIdentifier(run.organizationName, run.projectName, runIdentificationPair.runId),
        {
          index: index + 1,
        },
      );
    }
  });

  return metadata;
}

export function entriesToIdsAndNames(
  entries: LeaderboardEntry[],
  toArray?: undefined,
): { runIds: Set<string>; experimentNames: Set<string> };
export function entriesToIdsAndNames(
  entries: LeaderboardEntry[],
  toArray: (values: Set<string>) => string[],
): { runIds: string[]; experimentNames: string[] };

export function entriesToIdsAndNames(
  entries: LeaderboardEntry[],
  toArray: (values: Set<string>) => string[] = identity,
): {
  runIds: string[] | Set<string>;
  experimentNames: string[] | Set<string>;
} {
  const runIds = new Set<string>();
  const experimentNames = new Set<string>();

  entries.forEach((run) => {
    const runIdentificationPair = getRunIdentificationPair(run);

    if (runIdentificationPair) {
      if (runIdentificationPair.experimentName) {
        experimentNames.add(runIdentificationPair.experimentName);
      }

      runIds.add(runIdentificationPair.runId);
    }
  });

  return { runIds: toArray(runIds), experimentNames: toArray(experimentNames) };
}

export function entriesToIdsOrNames(
  entries: LeaderboardEntry[],
  extractNames?: boolean,
  toArray?: undefined,
): { runIds: Set<string>; experimentNames: Set<string> };
export function entriesToIdsOrNames(
  entries: LeaderboardEntry[],
  extractNames?: boolean,
  toArray?: (values: Set<string>) => string[],
): { runIds: string[]; experimentNames: string[] };

export function entriesToIdsOrNames(
  entries: LeaderboardEntry[],
  extractNames = true,
  toArray: (values: Set<string>) => string[] = identity,
): {
  runIds: string[] | Set<string>;
  experimentNames: string[] | Set<string>;
} {
  const runIds: Set<string> = new Set();
  const experimentNames: Set<string> = new Set();

  entries.forEach((run) => {
    const runIdentificationPair = getRunIdentificationPair(run, extractNames);

    if (runIdentificationPair) {
      if (runIdentificationPair.experimentName && extractNames) {
        experimentNames.add(runIdentificationPair.experimentName);
      }

      if (!extractNames) {
        runIds.add(runIdentificationPair.runId);
      }
    }
  });

  return { runIds: toArray(runIds), experimentNames: toArray(experimentNames) };
}
